import { observer } from "mobx-react-lite";
import { DatabaseIcon, ListTree } from "lucide-react";

import { XmlPathConfiguration } from "@/types/evaluate";
import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { SingleSourceSelector } from "@/components/PowerBlock/Block/SourceSelectors";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import Checkbox from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

const validationSchema = createBlockSchema<XmlPathConfiguration>()
  .requireName()
  .addRule(required("configuration.xml_path", "XML path is required"))
  .addRule(required("configuration.source", "Source is required"))
  .build();

const XMLExtractionBlockCore = observer(
  ({ sources = createEmptySources() }: Omit<BlockProps<XmlPathConfiguration>, "DefaultFields">) => {
    const block = useBlock<XmlPathConfiguration>();
    const isReadonly = useIsReadonly();

    const handleXmlPathChange = (path: string) => {
      block.setConfiguration({
        ...block.configuration,
        xml_path: path,
        type: block.configuration?.type || "find",
        source: block.configuration?.source || "",
        return_text: block.configuration?.return_text ?? false,
      });
    };

    const handleTypeChange = (type: "find" | "findall") => {
      block.setConfiguration({
        ...block.configuration,
        xml_path: block.configuration?.xml_path || "",
        type,
        source: block.configuration?.source || "",
        return_text: block.configuration?.return_text ?? false,
      });
    };

    const handleReturnTextChange = (returnText: boolean) => {
      block.setConfiguration({
        ...block.configuration,
        xml_path: block.configuration?.xml_path || "",
        type: block.configuration?.type || "find",
        source: block.configuration?.source || "",
        return_text: returnText,
      });
    };

    return (
      <>
        <SectionGroup
          title="XML Path Configuration"
          description="Configure how to extract data from XML documents"
          icon={<ListTree className="h-4 w-4" />}
        >
          <div className="space-y-4">
            <ValidatedField name="XML Path" path="configuration.xml_path">
              <Input
                className="w-full"
                value={block.configuration?.xml_path || ""}
                onChange={e => handleXmlPathChange(e.target.value)}
                placeholder="e.g. .//element"
                disabled={isReadonly}
              />
            </ValidatedField>

            <ValidatedField name="Find All Matches" path="">
              <Checkbox
                id="find-all-matches"
                label="Find all matches"
                checked={block.configuration?.type === "findall"}
                onChange={e => handleTypeChange(e.target.checked ? "findall" : "find")}
                disabled={isReadonly}
              />
            </ValidatedField>

            <ValidatedField name="Return Text" path="">
              <Checkbox
                id="return-text"
                label="Return text content instead of elements"
                checked={block.configuration?.return_text ?? false}
                onChange={e => handleReturnTextChange(e.target.checked)}
                disabled={isReadonly}
              />
            </ValidatedField>
          </div>
        </SectionGroup>

        <SectionGroup
          title="Data Source"
          description="Select the XML source to extract data from"
          icon={<DatabaseIcon className="h-4 w-4" />}
        >
          <SingleSourceSelector
            sources={sources}
            sourceKey="source"
            validationPath="configuration.source"
            name="Source"
          />
        </SectionGroup>
      </>
    );
  },
);

export default withBlockStore<XmlPathConfiguration>(XMLExtractionBlockCore, {
  validationSchema,
});
